// src/App.js
import { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useHousehold } from "./HouseholdContext";
import AuthPage from "./components/AuthPage";
import Header from "./components/Header";
import Dashboard from "./components/Dashboard";
import TaskList from "./components/TaskList";
import TaskForm from "./components/TaskForm";
import TaskDetail from "./components/TaskDetail";
import Schedule from "./components/Schedule";
import HouseholdSetup from "./components/HouseholdSetup";
import HouseholdSettings from "./components/HouseholdSettings";
import SettingsPage from "./components/SettingsPage";
import AcceptInvite from "./components/AcceptInvite";
import InviteNotification from "./components/InviteNotification";
import { subscribeToTasks, addTask, updateTask, deleteTask } from "./taskService";
import { defaultTask } from "./constants";
import "./App.css";

export default function App() {
  const { user } = useAuth();
  const { household, setHousehold } = useHousehold();

  const params = new URLSearchParams(window.location.search);
  const inviteToken = params.get("invite");

  if (user === undefined || household === undefined) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fdf6ec", fontFamily: "'Fredoka One', cursive", fontSize: 24, color: "#8B4513" }}>
        <div style={{ textAlign: "center" }}>
          <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Homie" style={{ width: 72, height: 72, objectFit: "contain", marginBottom: 12 }} />
          <div>Loading Homie...</div>
        </div>
      </div>
    );
  }

  if (!user) return <AuthPage />;

  if (inviteToken) {
    return (
      <AcceptInvite
        token={inviteToken}
        onAccepted={(newHousehold) => { setHousehold(newHousehold); window.history.replaceState({}, "", window.location.pathname); }}
        onDeclined={() => { window.history.replaceState({}, "", window.location.pathname); }}
      />
    );
  }

  if (!household) {
    return <HouseholdSetup onComplete={(newHousehold) => setHousehold(newHousehold)} />;
  }

  return <AppInner />;
}

function AppInner() {
  const { user } = useAuth();
  const { household, setHousehold } = useHousehold();
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState("dashboard");
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState({ ...defaultTask });
  const [saving, setSaving] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [taskFilter, setTaskFilter] = useState("all");

  useEffect(() => {
    if (!household?.id) return;
    setLoadingTasks(true);
    const unsub = subscribeToTasks(household.id, (newTasks) => {
      setTasks(newTasks);
      setLoadingTasks(false);
    });
    return unsub;
  }, [household?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedTask) {
      const fresh = tasks.find(t => t.id === selectedTask.id);
      if (fresh) setSelectedTask(fresh);
    }
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  function goToAdd()        { setEditingTask({ ...defaultTask }); setView("add"); }
  function goToEdit(task)   { setEditingTask({ ...task }); setView("edit"); }
  function goToDetail(task) { setSelectedTask(task); setView("detail"); }
  function goToTasks()      { setTaskFilter("all"); setView("tasks"); }

  async function handleSave(formData) {
    setSaving(true);
    try {
      if (formData.id) {
        const { id, createdAt, ...rest } = formData;
        await updateTask(household.id, id, rest);
        setSelectedTask({ ...formData, id });
        setView("detail");
      } else {
        await addTask(household.id, formData);
        setView("tasks");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(task) {
    setSaving(true);
    try {
      await deleteTask(household.id, task.id);
      setView("tasks");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateSteps(taskId, completedSteps) {
    await updateTask(household.id, taskId, { completedSteps });
    if (selectedTask?.id === taskId) setSelectedTask(t => ({ ...t, completedSteps }));
  }

  async function handleAddRecommendedTask(taskData) {
    await addTask(household.id, taskData);
  }

  async function handleSaveChat(taskId, chatHistory) {
    await updateTask(household.id, taskId, { chatHistory });
    // Update selectedTask immediately so chat persists without refetch
    if (selectedTask?.id === taskId) setSelectedTask(t => ({ ...t, chatHistory }));
  }

  async function handleToggleComplete(task) {
    const updated = { completed: !task.completed };
    await updateTask(household.id, task.id, updated);
    if (selectedTask?.id === task.id) setSelectedTask(t => ({ ...t, ...updated }));
  }

  const taskCounts = {
    open: tasks.filter(t => !t.completed).length,
    overdue: tasks.filter(t => !t.completed && t.dueDate && new Date(t.dueDate) < new Date()).length,
  };

  const viewTitles = {
    dashboard: "Dashboard", tasks: "All Tasks", add: "New Task",
    edit: "Edit Task", detail: "Task Details", calendar: "Schedule",
    household: "Household", settings: "Settings",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#fdf6ec", display: "flex", flexDirection: "column" }}>
      <Header
        currentView={view}
        setView={setView}
        setEditingTask={setEditingTask}
        defaultTask={defaultTask}
        taskCounts={taskCounts}
      />

      <main style={{ flex: 1, padding: 28, maxWidth: 1100, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
          <h1 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 28, color: "#8B4513" }}>
            {viewTitles[view] || ""}
          </h1>
          {view !== "settings" && (
            <div
              onClick={() => setView("household")}
              style={{ display: "flex", alignItems: "center", gap: 8, background: view === "household" ? "#fed7aa" : "#fff3e0", borderRadius: 12, padding: "6px 14px", border: "1px solid #fed7aa", cursor: "pointer", transition: "all 0.18s" }}
            >
              <span style={{ fontSize: 16 }}>🏠</span>
              <span style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 14, color: "#8B4513" }}>{household.name}</span>
              <span style={{ fontSize: 11, color: "#c09870", fontWeight: 700 }}>· {household.members?.length || 1} member{household.members?.length !== 1 ? "s" : ""}</span>
            </div>
          )}
        </div>

        {loadingTasks && view !== "add" && view !== "household" && view !== "settings" ? (
          <div style={{ textAlign: "center", padding: 60, color: "#c09870", fontFamily: "'Fredoka One', cursive", fontSize: 20 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
            Loading your tasks...
          </div>
        ) : (
          <>
            {view === "dashboard" && <Dashboard tasks={tasks} setSelectedTask={setSelectedTask} setView={setView} setTaskFilter={setTaskFilter} />}
            {view === "tasks"     && <TaskList tasks={tasks} onSelect={goToDetail} onToggleComplete={handleToggleComplete} onAdd={goToAdd} initialFilter={taskFilter} />}
            {view === "add"       && <TaskForm task={{ ...defaultTask }} onSave={handleSave} onCancel={goToTasks} saving={saving} />}
            {view === "edit"      && <TaskForm task={editingTask} onSave={handleSave} onCancel={() => setView(selectedTask ? "detail" : "tasks")} saving={saving} />}
            {view === "detail"    && selectedTask && <TaskDetail task={selectedTask} onEdit={() => goToEdit(selectedTask)} onDelete={() => handleDelete(selectedTask)} onToggleComplete={handleToggleComplete} onBack={goToTasks} saving={saving} onSaveChat={handleSaveChat} onUpdateSteps={handleUpdateSteps} onAddRecommendedTask={handleAddRecommendedTask} />}
            {view === "calendar"  && <Schedule tasks={tasks} onSelect={goToDetail} />}
            {view === "household" && <HouseholdSettings onHouseholdLeft={() => setHousehold(null)} />}
            {view === "settings"  && <SettingsPage />}
          </>
        )}
      </main>
      <InviteNotification />
    </div>
  );
}
